/**
 * Capital (主城) - Grand palace with tiered roofs and flag
 * Features: Large stone platform, multi-tier structure, ornate roofs, lanterns, flag
 */

import * as THREE from 'three';
import { createLantern, createFlag, createCurvedRoof } from '../BuildingMaterials';

export function createCapitalMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.9 });
  const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 });
  const wallMat = new THREE.MeshStandardMaterial({ color: mainColor, roughness: 0.7 });
  const roofMat = new THREE.MeshStandardMaterial({ color: accentColor, roughness: 0.6 });
  const darkRoofMat = new THREE.MeshStandardMaterial({ color: accentColor.clone().multiplyScalar(0.7), roughness: 0.5 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.8 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.85 });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xd4af37, roughness: 0.3, metalness: 0.6 });
  const doorMat = new THREE.MeshStandardMaterial({ color: 0x8b0000, roughness: 0.7 });
  const windowMat = new THREE.MeshStandardMaterial({ color: 0x1a237e, emissive: 0x0d47a1, emissiveIntensity: 0.15 });
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xc62828,
    emissive: 0xff3d00,
    emissiveIntensity: 0.4
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.5
  });

  // === Grand Stone Platform Base ===
  const base1Geom = new THREE.BoxGeometry(3.8, 0.35, 3.8);
  const base1 = new THREE.Mesh(base1Geom, darkStoneMat);
  base1.position.y = 0.175;
  base1.castShadow = true;
  base1.receiveShadow = true;
  group.add(base1);

  const base2Geom = new THREE.BoxGeometry(3.4, 0.3, 3.4);
  const base2 = new THREE.Mesh(base2Geom, stoneMat);
  base2.position.y = 0.5;
  base2.castShadow = true;
  group.add(base2);

  const base3Geom = new THREE.BoxGeometry(3.0, 0.25, 3.0);
  const base3 = new THREE.Mesh(base3Geom, darkStoneMat);
  base3.position.y = 0.775;
  base3.castShadow = true;
  group.add(base3);

  // Grand steps (wider and more impressive)
  for (let i = 0; i < 5; i++) {
    const stepWidth = 1.4 - i * 0.08;
    const stepGeom = new THREE.BoxGeometry(stepWidth, 0.12, 0.25);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.06 + i * 0.15, 1.9 + i * 0.2);
    step.castShadow = true;
    group.add(step);
  }

  // === Main Building - First Tier (Largest) ===
  const mainGeom = new THREE.BoxGeometry(2.8, 1.4, 2.8);
  const main = new THREE.Mesh(mainGeom, wallMat);
  main.position.y = 1.6;
  main.castShadow = true;
  main.receiveShadow = true;
  group.add(main);

  // Wall decorative trim
  const trimGeom = new THREE.BoxGeometry(2.85, 0.08, 2.85);
  const trim1 = new THREE.Mesh(trimGeom, goldMat);
  trim1.position.y = 1.0;
  group.add(trim1);

  const trim2 = new THREE.Mesh(trimGeom.clone(), goldMat);
  trim2.position.y = 2.25;
  group.add(trim2);

  // === First Tier Roof ===
  const roof1BaseGeom = new THREE.BoxGeometry(3.2, 0.15, 3.2);
  const roof1Base = new THREE.Mesh(roof1BaseGeom, roofMat);
  roof1Base.position.y = 2.4;
  roof1Base.castShadow = true;
  group.add(roof1Base);

  const roof1ConeGeom = new THREE.ConeGeometry(2.3, 0.8, 4);
  const roof1Cone = new THREE.Mesh(roof1ConeGeom, roofMat);
  roof1Cone.position.y = 2.85;
  roof1Cone.rotation.y = Math.PI / 4;
  roof1Cone.castShadow = true;
  group.add(roof1Cone);

  // Roof eave
  const eave1Geom = new THREE.BoxGeometry(3.4, 0.08, 3.4);
  const eave1 = new THREE.Mesh(eave1Geom, darkRoofMat);
  eave1.position.y = 2.35;
  group.add(eave1);

  // Corner ornaments
  const cornerOrnGeom = new THREE.ConeGeometry(0.08, 0.15, 4);
  const corner1Positions = [
    { x: 1.65, z: 1.65 }, { x: -1.65, z: 1.65 },
    { x: 1.65, z: -1.65 }, { x: -1.65, z: -1.65 }
  ];
  for (const pos of corner1Positions) {
    const orn = new THREE.Mesh(cornerOrnGeom, darkRoofMat);
    orn.position.set(pos.x, 2.28, pos.z);
    orn.rotation.x = Math.PI;
    group.add(orn);
  }

  // === Second Tier ===
  const tier2Geom = new THREE.BoxGeometry(2.0, 1.0, 2.0);
  const tier2 = new THREE.Mesh(tier2Geom, wallMat);
  tier2.position.y = 3.8;
  tier2.castShadow = true;
  group.add(tier2);

  // Second tier roof
  const roof2BaseGeom = new THREE.BoxGeometry(2.4, 0.12, 2.4);
  const roof2Base = new THREE.Mesh(roof2BaseGeom, roofMat);
  roof2Base.position.y = 4.35;
  roof2Base.castShadow = true;
  group.add(roof2Base);

  const roof2ConeGeom = new THREE.ConeGeometry(1.6, 0.6, 4);
  const roof2Cone = new THREE.Mesh(roof2ConeGeom, roofMat);
  roof2Cone.position.y = 4.7;
  roof2Cone.rotation.y = Math.PI / 4;
  roof2Cone.castShadow = true;
  group.add(roof2Cone);

  const eave2Geom = new THREE.BoxGeometry(2.6, 0.06, 2.6);
  const eave2 = new THREE.Mesh(eave2Geom, darkRoofMat);
  eave2.position.y = 4.3;
  group.add(eave2);

  // === Third Tier (Top) ===
  const tier3Geom = new THREE.BoxGeometry(1.2, 0.7, 1.2);
  const tier3 = new THREE.Mesh(tier3Geom, wallMat);
  tier3.position.y = 5.35;
  tier3.castShadow = true;
  group.add(tier3);

  const roof3ConeGeom = new THREE.ConeGeometry(1.0, 0.5, 4);
  const roof3Cone = new THREE.Mesh(roof3ConeGeom, roofMat);
  roof3Cone.position.y = 5.95;
  roof3Cone.rotation.y = Math.PI / 4;
  roof3Cone.castShadow = true;
  group.add(roof3Cone);

  const eave3Geom = new THREE.BoxGeometry(1.5, 0.06, 1.5);
  const eave3 = new THREE.Mesh(eave3Geom, darkRoofMat);
  eave3.position.y = 5.72;
  group.add(eave3);

  // === Top Spire and Ornament ===
  const spireGeom = new THREE.ConeGeometry(0.1, 0.35, 8);
  const spire = new THREE.Mesh(spireGeom, goldMat);
  spire.position.y = 6.38;
  group.add(spire);

  const topOrnGeom = new THREE.SphereGeometry(0.12, 8, 6);
  const topOrn = new THREE.Mesh(topOrnGeom, goldMat);
  topOrn.position.y = 6.6;
  group.add(topOrn);

  // === Grand Door ===
  const doorFrameGeom = new THREE.BoxGeometry(0.1, 1.1, 0.18);
  const doorFrameLeft = new THREE.Mesh(doorFrameGeom, darkWoodMat);
  doorFrameLeft.position.set(-0.45, 1.45, 1.43);
  group.add(doorFrameLeft);

  const doorFrameRight = new THREE.Mesh(doorFrameGeom.clone(), darkWoodMat);
  doorFrameRight.position.set(0.45, 1.45, 1.43);
  group.add(doorFrameRight);

  const doorTopGeom = new THREE.BoxGeometry(1.05, 0.12, 0.18);
  const doorTop = new THREE.Mesh(doorTopGeom, darkWoodMat);
  doorTop.position.set(0, 2.05, 1.43);
  group.add(doorTop);

  // Red doors
  const doorGeom = new THREE.BoxGeometry(0.38, 1.0, 0.08);
  const leftDoor = new THREE.Mesh(doorGeom, doorMat);
  leftDoor.position.set(-0.22, 1.4, 1.44);
  group.add(leftDoor);

  const rightDoor = new THREE.Mesh(doorGeom.clone(), doorMat);
  rightDoor.position.set(0.22, 1.4, 1.44);
  group.add(rightDoor);

  // Gold door handles
  const handleGeom = new THREE.SphereGeometry(0.04, 6, 5);
  const leftHandle = new THREE.Mesh(handleGeom, goldMat);
  leftHandle.position.set(-0.12, 1.45, 1.5);
  group.add(leftHandle);

  const rightHandle = new THREE.Mesh(handleGeom.clone(), goldMat);
  rightHandle.position.set(0.12, 1.45, 1.5);
  group.add(rightHandle);

  // Interior glow
  const glowGeom = new THREE.PlaneGeometry(0.7, 0.9);
  const glow = new THREE.Mesh(glowGeom, glowMat);
  glow.position.set(0, 1.4, 1.38);
  group.add(glow);

  // === Windows ===
  const windowGeom = new THREE.BoxGeometry(0.35, 0.5, 0.08);
  const windowPositions = [
    { x: -0.9, z: 1.42 }, { x: 0.9, z: 1.42 },
    { x: 1.42, z: -0.5 }, { x: 1.42, z: 0.5 },
    { x: -1.42, z: -0.5 }, { x: -1.42, z: 0.5 },
    { x: -0.5, z: -1.42 }, { x: 0.5, z: -1.42 },
  ];
  for (const pos of windowPositions) {
    const win = new THREE.Mesh(windowGeom, windowMat);
    win.position.set(pos.x, 1.7, pos.z);
    if (Math.abs(pos.x) > 1.2) win.rotation.y = Math.PI / 2;
    group.add(win);

    // Window frame
    const frameGeom = new THREE.BoxGeometry(0.42, 0.58, 0.1);
    const frame = new THREE.Mesh(frameGeom, darkWoodMat);
    frame.position.set(pos.x, 1.7, pos.z);
    if (Math.abs(pos.x) > 1.2) frame.rotation.y = Math.PI / 2;
    group.add(frame);
  }

  // Second tier windows
  for (const pos of [{ x: 0, z: 1.02 }, { x: 0, z: -1.02 }, { x: 1.02, z: 0 }, { x: -1.02, z: 0 }]) {
    const win = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.4, 0.06), windowMat);
    win.position.set(pos.x, 3.85, pos.z);
    if (Math.abs(pos.x) > 0.9) win.rotation.y = Math.PI / 2;
    group.add(win);
  }

  // === Lanterns ===
  createLantern(group, -0.75, 1.95, 1.6, lanternMat);
  createLantern(group, 0.75, 1.95, 1.6, lanternMat);

  // === Flag ===
  const flagMat = new THREE.MeshStandardMaterial({
    color: mainColor,
    side: THREE.DoubleSide,
    roughness: 0.9
  });
  createFlag(group, 0, 5.75, 0, 1.6, 0.65, 0.45, woodMat, flagMat);

  // === Support Pillars on Platform ===
  const pillarGeom = new THREE.CylinderGeometry(0.08, 0.1, 1.5, 8);
  const pillarPositions = [
    { x: -1.3, z: 1.5 }, { x: 1.3, z: 1.5 },
    { x: -1.3, z: -1.5 }, { x: 1.3, z: -1.5 },
    { x: 1.5, z: -1.3 }, { x: 1.5, z: 1.3 },
    { x: -1.5, z: -1.3 }, { x: -1.5, z: 1.3 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, new THREE.MeshStandardMaterial({
      color: mainColor.clone().multiplyScalar(0.9),
      roughness: 0.7
    }));
    pillar.position.set(pos.x, 1.65, pos.z);
    pillar.castShadow = true;
    group.add(pillar);

    // Gold pillar base
    const baseGeom = new THREE.CylinderGeometry(0.12, 0.14, 0.08, 8);
    const pillarBase = new THREE.Mesh(baseGeom, goldMat);
    pillarBase.position.set(pos.x, 0.95, pos.z);
    group.add(pillarBase);

    // Gold pillar top
    const topGeom = new THREE.CylinderGeometry(0.12, 0.1, 0.06, 8);
    const pillarTop = new THREE.Mesh(topGeom, goldMat);
    pillarTop.position.set(pos.x, 2.35, pos.z);
    group.add(pillarTop);
  }

  // === Decorative Stone Lions ===
  const createLion = (x: number, z: number, facing: number) => {
    // Body
    const bodyGeom = new THREE.BoxGeometry(0.25, 0.2, 0.35);
    const lionMat = new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.8 });
    const body = new THREE.Mesh(bodyGeom, lionMat);
    body.position.set(x, 0.95, z);
    body.rotation.y = facing;
    group.add(body);

    // Head
    const headGeom = new THREE.BoxGeometry(0.18, 0.18, 0.18);
    const head = new THREE.Mesh(headGeom, lionMat);
    head.position.set(x, 1.1, z + Math.cos(facing) * 0.15);
    head.rotation.y = facing;
    group.add(head);
  };

  createLion(-0.7, 1.65, 0);
  createLion(0.7, 1.65, 0);

  // === Incense Burner ===
  const burnerGeom = new THREE.CylinderGeometry(0.18, 0.22, 0.3, 8);
  const burnerMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.7 });
  const burner = new THREE.Mesh(burnerGeom, burnerMat);
  burner.position.set(0, 0.95, 1.7);
  group.add(burner);

  // Smoke
  const smokeMat = new THREE.MeshStandardMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.4
  });
  for (let i = 0; i < 3; i++) {
    const smokeGeom = new THREE.SphereGeometry(0.05 - i * 0.01, 5, 4);
    const smoke = new THREE.Mesh(smokeGeom, smokeMat);
    smoke.position.set(0, 1.15 + i * 0.12, 1.7);
    group.add(smoke);
  }
}
